import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { APIError } from "@coras-io/web-components/errors";
import "@coras-io/web-components/base.css";
import "./coras.css";
import "./index.css";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

// Create a new router instance
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    tracesSampleRate: Number(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.2,
    ),
    profilesSampleRate: Number(
      import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE || 0.2,
    ),
    normalizeDepth: 6,
    ignoreErrors: [
      // Third-party scripts accessing cross-origin stylesheets (Firefox SecurityError)
      /CSSStyleSheet\.cssRules getter.*cross-origin stylesheet/,
      // Stale chunks after deployment — user has cached HTML pointing to old hashed assets
      /Failed to fetch dynamically imported module/,
      // Third-party scripts rejecting promises with CustomEvent objects instead of Errors
      /Event `CustomEvent` \(type=unhandledrejection\) captured as promise rejection/,
    ],
    integrations: [
      Sentry.browserProfilingIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({ levels: ["warn", "error"] }),
      Sentry.tanstackRouterBrowserTracingIntegration(router),
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Attach full API response context for APIError instances
      if (error instanceof APIError) {
        event.contexts = {
          ...event.contexts,
          apiError: {
            endpoint: error.endpoint,
            method: error.method,
            statusCode: error.statusCode,
            // Stringify to bypass Sentry's key-name-based data scrubbing
            // (field names like "transaction_hash" trigger filtering)
            response: JSON.stringify(error.response),
          },
        };
      }

      return event;
    },
  });
}

// Render the app
const rootElement = document.getElementById("root")!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  });

  root.render(
    <StrictMode>
      <Sentry.ErrorBoundary>
        <RouterProvider router={router} />
      </Sentry.ErrorBoundary>
    </StrictMode>,
  );
}
